import { emptyString, emptyArray } from 'constants/emptyConst';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Grid } from '@mui/material';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { getPagingLimits, filterByValue } from './common/functions';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FinancialProductListComponent from './FinancialProductListComponent';
import * as eventTypes from 'actions/eventTypes/offer/financials/offerFinancingStructure';
import { getAvailableFinancialProducts } from 'actions/actionCreators/offer/financials/offerFinancingStructureActions';
import { Button, Dialog } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

const ChangeFinancialProductModal = ({ onModalClose, documentId, siteId, contractTypeId }) => {
  const defaultPager = useRef({ page: 1, pageSize: 3 });
  const rowsPerPageOptions = useRef([3, 6, 12]);
  const DEBOUNCE_DELAY = useRef(500);

  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [filter, setFilter] = useState(emptyString);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [data, setData] = useState(emptyArray);
  const [dataOnPage, setDataOnPage] = useState(emptyArray);
  const [pager, setPager] = useState(defaultPager.current);
  const [totalCount, setTotalCount] = useState(0);

  const onProductSelection = useCallback(
    financialProductId =>
      setData(data.map(item => (item.id === financialProductId ? { ...item, selecting: true, selected: false, hasError: false } : item))),
    [data]
  );

  const onProductSelected = useCallback(
    (financialProductId, hasError = false) => {
      if (hasError)
        setData(data.map(item => (item.id === financialProductId ? { ...item, selecting: false, selected: false, hasError: true } : item)));
      else
        setData(data.map(item => (item.id === financialProductId ? { ...item, selecting: false, selected: true, hasError: false } : item)));
    },
    [data]
  );

  const getDisabledStatus = useCallback(
    () => ((data?.length ?? 0) === 0 || dataLoading ? true : data.some(item => item.selecting || item.selected)),
    [data, dataLoading]
  );

  const onDataArrival = useCallback((data, hasError) => {
    if (hasError) {
      setDataLoading(false);
      setDataLoaded(false);
    } else {
      setDataLoading(false);
      setDataLoaded(true);
      setData(data);
    }
  }, []);

  useEffect(() => {
    if (!dataLoaded) {
      dispatch(getAvailableFinancialProducts(documentId, siteId, contractTypeId, (data, hasError) => onDataArrival(data, hasError)));
    }
  }, [dispatch, documentId, siteId, contractTypeId, dataLoaded, onDataArrival]);

  useEffect(() => {
    const elemSelected = data.some(item => item.selected);
    if (elemSelected) {
      setTimeout(() => {
        onModalClose();
        dispatch({
          type: eventTypes.OFFER_FINANCING_STRUCTURE_INVALIDATED
        });
      }, 1000);
    }
  }, [data, onModalClose, dispatch]);

  useEffect(() => {
    const elemHasError = data.some(item => item.hasError);
    if (elemHasError) {
      setTimeout(() => {
        onModalClose();
      }, 1000);
    }
  }, [data, onModalClose]);

  //#region pagination and filtering
  const pushFiltersAndPaging = useCallback(
    (filter, pager) => {
      setFilter(filter);
      setPager(pager);

      const { indexOfFirstItem, indexOfLastItem } = getPagingLimits(pager);

      if (filter !== emptyString) {
        const filteredFinancialProducts = filterByValue(data, filter);
        const currentFinancialProducts = filteredFinancialProducts.slice(indexOfFirstItem, indexOfLastItem);

        setDataOnPage(currentFinancialProducts);
        setTotalCount(filteredFinancialProducts.length);
      } else {
        setDataOnPage(data.slice(indexOfFirstItem, indexOfLastItem));
        setTotalCount(data.length);
      }
    },
    [data]
  );

  useEffect(() => {
    if (data) {
      const pagingLimits = getPagingLimits(pager);

      setDataOnPage(data.slice(pagingLimits.indexOfFirstItem, pagingLimits.indexOfLastItem));
      setTotalCount(data.length);
    }
  }, [pager, data]);

  useEffect(() => {
    pushFiltersAndPaging(filter, pager);
  }, [pager, filter, pushFiltersAndPaging]);

  const onFilterValueChangedDebounce = () => setPager(defaultPager.current);

  const filterChangedDebounced = useRef(debounce(onFilterValueChangedDebounce, DEBOUNCE_DELAY.current));

  const handleChangePage = page => {
    const newPager = Object.assign({}, pager, { page, pageSize: pager.pageSize });
    pushFiltersAndPaging(filter, newPager);
  };

  const handleChangeRowsPerPage = pageSize => {
    const newPager = Object.assign({}, pager, { page: 1, pageSize: pageSize });
    pushFiltersAndPaging(filter, newPager);
  };

  const handleRefresh = () => setPager(defaultPager.current);

  const handleFilterValueChanged = (value, debounced) => {
    setFilter(value);

    if (debounced) filterChangedDebounced.current(value);
    else setPager(defaultPager.current);
  };

  const handleResetFilters = () => {
    setFilter(emptyString);
    setPager(defaultPager.current);
  };

  //#endregion pagination and filtering

  return (
    <Dialog
      open={true}
      maxWidth='lg'
      fullWidth
      disableEscapeKeyDown
      title={t('OfferEdit.FinancingStructure.FinancialProductChangeModal.Title')}
      content={
        <Grid container>
          <FinancialProductListComponent
            availableFinancialProducts={dataOnPage}
            onFilterValueChanged={handleFilterValueChanged}
            fullTextFilter={filter}
            onResetFilters={handleResetFilters}
            documentId={documentId}
            siteId={siteId}
            onProductSelection={onProductSelection}
            onProductSelected={onProductSelected}
            loading={dataLoading}
            loaded={dataLoaded}
            disabledStatus={getDisabledStatus()}
          />
          <Grid item xs={12} container justifyContent='flex-end'>
            <PaginationWrapper
              count={totalCount}
              pageSize={pager.pageSize}
              page={pager.page}
              rowsPerPageOptions={rowsPerPageOptions.current}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onPageChange={handleChangePage}
              onRefresh={handleRefresh}
              buttonsDisabled={getDisabledStatus()}
            />
          </Grid>
        </Grid>
      }
      actions={
        <BoxActionsWrapper forDialog>
          <Button
            id={'offerFinancingStructureCard_financialProductChangeModal_closeBtn'}
            color='primary'
            variant='outlined'
            onClick={onModalClose}
          >
            {t('Close')}
          </Button>
        </BoxActionsWrapper>
      }
    />
  );
};

ChangeFinancialProductModal.propTypes = {
  onModalClose: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  contractTypeId: PropTypes.number.isRequired
};

export default ChangeFinancialProductModal;
